import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, User, Phone, Calendar, Award, Bell, BellOff, Shield, Camera, GraduationCap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, isSubscribed } from '../utils/pushNotifications';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    age: user?.age || '',
    phone: user?.phone || '',
    level: user?.level || 1,
    gender: user?.gender || ''
  });

  useEffect(() => {
    checkNotificationStatus();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/check`, {
        withCredentials: true
      });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un\'immagine valida');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande. Max 5MB');
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/users/me/picture`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      updateUser(response.data.user);
      toast.success('Foto profilo aggiornata!');
    } catch (error) {
      console.error('Error uploading picture:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingPicture(false);
    }
  };

  const profilePictureUrl = getAvatarUrl(user);

  const checkNotificationStatus = async () => {
    const subscribed = await isSubscribed();
    setNotificationsEnabled(subscribed);
  };

  const toggleNotifications = async () => {
    if (!isPushSupported()) {
      toast.error('Le notifiche push non sono supportate su questo dispositivo');
      return;
    }

    setNotificationsLoading(true);
    try {
      if (notificationsEnabled) {
        await unsubscribeFromPush();
        setNotificationsEnabled(false);
        toast.success('Notifiche disattivate');
      } else {
        const subscription = await subscribeToPush();
        if (subscription) {
          setNotificationsEnabled(true);
          toast.success('Notifiche attivate! Riceverai avvisi per i nuovi messaggi');
        } else {
          toast.error('Impossibile attivare le notifiche. Controlla i permessi del browser.');
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast.error('Errore nella gestione delle notifiche');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'age' ? (value ? parseInt(value) : '') : value
    }));
  };

  const handleLevelChange = (level) => {
    setFormData(prev => ({ ...prev, level }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(
        `${API_URL}/api/users/me`,
        {
          name: formData.name,
          surname: formData.surname || null,
          age: formData.age || null,
          phone: formData.phone || null,
          level: formData.level
        },
        { withCredentials: true }
      );
      
      updateUser(response.data);
      toast.success('Profilo aggiornato!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Errore nell\'aggiornamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const levelNames = ['Principiante', 'Base', 'Intermedio', 'Avanzato', 'Esperto'];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-outfit text-2xl font-semibold text-slate-900">
          Il Mio Profilo
        </h1>
      </div>

      {/* Avatar Section */}
      <div className="bg-white rounded-3xl p-8 border border-slate-100 mb-6 text-center">
        <div className="relative inline-block">
          <div 
            onClick={handlePictureClick}
            className="w-28 h-28 bg-sky-400 rounded-3xl flex items-center justify-center text-white text-3xl font-semibold mx-auto cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
          >
            {uploadingPicture ? (
              <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
            ) : profilePictureUrl ? (
              <img src={profilePictureUrl} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0)
            )}
          </div>
          <button
            onClick={handlePictureClick}
            disabled={uploadingPicture}
            className="absolute bottom-0 right-0 w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center text-white shadow-lg hover:bg-sky-600 transition-colors"
          >
            <Camera className="w-5 h-5" />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePictureChange}
          className="hidden"
        />
        <p className="text-slate-500 mt-4">{user?.email}</p>
        <p className="text-sm text-slate-400 mt-1">Tocca la foto per cambiarla</p>
      </div>

      {/* Notifications */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900 flex items-center gap-2">
              {notificationsEnabled ? (
                <Bell className="w-5 h-5 text-sky-500" />
              ) : (
                <BellOff className="w-5 h-5 text-slate-400" />
              )}
              Notifiche Push
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Ricevi avvisi per nuovi messaggi
            </p>
          </div>
          <button
            type="button"
            onClick={toggleNotifications}
            disabled={notificationsLoading}
            data-testid="toggle-notifications"
            className={`relative w-14 h-8 rounded-full transition-colors ${
              notificationsEnabled ? 'bg-sky-400' : 'bg-slate-200'
            }`}
          >
            <span
              className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                notificationsEnabled ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {!isPushSupported() && (
          <p className="text-xs text-amber-600 mt-2">
            Le notifiche push richiedono che l'app sia aggiunta alla schermata Home (su iPhone: Safari &gt; Condividi &gt; Aggiungi a Home)
          </p>
        )}
        {notificationsEnabled && (
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await axios.post(`${API_URL}/api/push/test`, {}, { withCredentials: true });
                if (res.data.success) {
                  toast.success('Notifica di test inviata! Controlla le notifiche.');
                } else {
                  toast.error(res.data.message);
                }
              } catch {
                toast.error('Errore nel test delle notifiche');
              }
            }}
            data-testid="test-notifications-btn"
            className="mt-3 w-full py-2.5 bg-sky-50 text-sky-600 border border-sky-200 rounded-xl text-sm font-medium hover:bg-sky-100 transition-colors"
          >
            Invia Notifica di Test
          </button>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <User className="w-4 h-4 inline mr-2" />
            Nome
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            data-testid="input-name"
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
            required
          />
        </div>

        {/* Surname */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <User className="w-4 h-4 inline mr-2" />
            Cognome
          </label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            data-testid="input-surname"
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
            placeholder="Inserisci il cognome"
          />
        </div>

        {/* Gender */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <User className="w-4 h-4 inline mr-2" />
            Sesso
          </label>
          <div className="flex gap-3">
            {[
              { value: 'M', label: 'Uomo' },
              { value: 'F', label: 'Donna' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, gender: option.value }))}
                data-testid={`gender-${option.value}`}
                className={`flex-1 px-4 py-3 rounded-2xl text-center font-medium transition-all ${
                  formData.gender === option.value
                    ? 'bg-sky-400 text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Age */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Calendar className="w-4 h-4 inline mr-2" />
            Età
          </label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleChange}
            data-testid="input-age"
            min="5"
            max="100"
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
            placeholder="Inserisci l'età"
          />
        </div>

        {/* Phone */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Phone className="w-4 h-4 inline mr-2" />
            Telefono
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            data-testid="input-phone"
            className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
            placeholder="+39 123 456 7890"
          />
        </div>

        {/* Level */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Award className="w-4 h-4 inline mr-2" />
            Livello di Gioco
          </label>
          
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => handleLevelChange(level)}
                data-testid={`level-${level}`}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                  formData.level === level
                    ? 'bg-sky-400 text-white'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`w-3 h-3 rounded-full ${
                        i <= level
                          ? formData.level === level ? 'bg-white' : 'bg-sky-400'
                          : formData.level === level ? 'bg-sky-300' : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-medium">{levelNames[level - 1]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          data-testid="save-profile-btn"
          className="w-full py-4 bg-sky-400 text-white rounded-full font-medium text-lg hover:bg-sky-500 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-5 h-5" />
              Salva Modifiche
            </>
          )}
        </button>

        {/* QR Code Link */}
        <button
          type="button"
          onClick={() => navigate('/qr-code')}
          data-testid="qr-code-btn"
          className="w-full py-4 bg-white text-slate-700 border border-slate-200 rounded-full font-medium hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          QR Code del Circolo
        </button>

        {/* Admin Panel Links - Only visible to admins */}
        {isAdmin && (
          <>
            <button
              type="button"
              onClick={() => navigate('/admin/gestionale')}
              data-testid="gestionale-btn"
              className="w-full py-4 bg-emerald-500 text-white rounded-full font-medium hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              Gestionale Lezioni
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              data-testid="admin-btn"
              className="w-full py-4 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-all flex items-center justify-center gap-2"
            >
              <Shield className="w-5 h-5" />
              Gestione Giocatori (Maestro)
            </button>
          </>
        )}

        {/* Logout */}
        <button
          type="button"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full py-4 bg-white text-red-500 border border-red-200 rounded-full font-medium hover:bg-red-50 transition-all"
        >
          Esci
        </button>
      </form>
    </div>
  );
};

export default ProfilePage;
